import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Play, Copy, Check, Clock, FileText } from 'lucide-react';
import type { HistoryItem } from '../services/history';
import { fetchUserHistory } from '../services/history';
import { useAuth } from '../hooks/useAuth';

export default function HistoryPage() {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyStatus, setCopyStatus] = useState<{ [key: string]: 'idle' | 'copied' }>({});
  const { user } = useAuth();

  useEffect(() => {
    async function loadHistory() {
      if (user?.id) {
        const data = await fetchUserHistory(user.id);
        setHistory(data);
        setLoading(false);
      }
    }
    loadHistory();
  }, [user]);

  const handleCopyPost = async (postId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopyStatus(prev => ({ ...prev, [postId]: 'copied' }));
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [postId]: 'idle' }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  function renderContent(item: HistoryItem) {
    const isTextAnalysis = !item.analysis.image_url;

    return (
      <div className="flex-shrink-0">
        {isTextAnalysis ? (
          <div className="w-48 h-48 bg-gray-50 rounded-lg shadow-md flex items-center justify-center">
            <div className="text-gray-500 flex flex-col items-center gap-2">
              <FileText className="w-8 h-8" />
              <div className="text-sm font-medium">Text Analysis</div>
            </div>
          </div>
        ) : (
          <img
            src={item.analysis.image_url}
            alt="LinkedIn Post"
            className="w-48 h-48 object-cover rounded-lg shadow-md"
          />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">No History Yet</h2>
        <p className="text-gray-600">Start analyzing LinkedIn posts to see your history here.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Your History</h1>
      <div className="space-y-8">
        {history.map((item) => (
          <div key={item.analysis.id} className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start space-x-6">
                {renderContent(item)}
                <div className="flex-1 min-w-0 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize">
                        {item.analysis.analysis_style}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {format(new Date(item.analysis.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                    {item.post?.status && (
                      <div className="flex items-center space-x-2">
                        {item.post.status === 'published' ? (
                          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            <Check className="w-4 h-4" />
                            <span>Published</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                            <Clock className="w-4 h-4" />
                            <span>Draft</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {!item.analysis.image_url && item.analysis.content && (
                    <div className="prose max-w-none">
                      <h3 className="text-lg font-semibold text-gray-900">Original Text</h3>
                      <div className="text-gray-600 line-clamp-3">
                        {item.analysis.content}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <span>AI Analysis Questions</span>
                      </h3>
                      <ul className="space-y-2 list-disc list-inside text-gray-600">
                        {item.analysis.questions.map((question, index) => (
                          <li key={index} className="text-gray-600">
                            {question}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {item.recording && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-gray-900">Your Response</h3>
                        <div className="flex items-center space-x-3">
                          <button className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                            <Play className="w-4 h-4" />
                            <span>Play Recording</span>
                          </button>
                          <span className="text-gray-500 text-sm">
                            {format(new Date(item.recording.created_at), 'h:mm a')}
                          </span>
                        </div>
                      </div>
                    )}

                    {item.post && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">Generated LinkedIn Post</h3>
                          <button
                            onClick={() => handleCopyPost(item.post!.id, item.post!.edited_content || item.post!.content)}
                            className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {copyStatus[item.post.id] === 'copied' ? (
                              <>
                                <Check className="w-4 h-4 text-green-600" />
                                <span className="text-green-600">Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                <span>Copy Post</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="prose max-w-none">
                          <p className="text-gray-600 whitespace-pre-wrap">
                            {item.post.edited_content || item.post.content}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
